import * as THREE from 'three';
import { Node, NodeMaterial } from 'three/webgpu';
import {
  Fn, uniform, vec3, vec4, float,
  fract, floor, sin, cos, mix, smoothstep, pow, max, abs, length, step, normalize, negate,
  positionLocal, cameraViewMatrix, cameraProjectionMatrix
} from 'three/tsl';

// Lunar sky for the "Boxing on the Moon" map. Reads deep-black, star-
// dominated, with a substantial procedural Earth disk hanging near the
// horizon. The Earth shader is the centrepiece — it layers continents,
// oceans, polar caps, day/night terminator, city lights, drifting clouds,
// and an atmospheric rim over a true ray-sphere projection so the disk
// reads as a sphere rather than a flat circle.
//
// Vertex trick matches SkyMaterial/SpaceMaterial: strip the camera
// translation (vec4(pos, 0) kills the translation column) then force
// clip-space z = w so after the perspective divide the depth is 1 — the
// sky renders behind everything else.
export interface MoonSkyOptions {
  /** When false, skip the Earth disk, its atmospheric halo, and the nebula
   *  fade-near-Earth. The shader becomes a pure starfield + nebulae — used
   *  by the pre-home splash screen, which wants the same cosmos look
   *  without the Earth hanging in view. */
  showEarth?: boolean;
  /** When false, skip the coloured nebula layer AND flatten spaceBase to
   *  pure deep-black. Used by the splash screen so the backdrop reads as
   *  clean space behind the logo instead of a purple-tinted nebula field. */
  showNebula?: boolean;
}

export class MoonSkyMaterial extends NodeMaterial {
  readonly uTime = uniform(0);
  // Direction toward Earth's centre in world space. Low above the horizon
  // on one side of the ring so players see it from ringside. Exposed so a
  // quick flip is one uniform write — consistent with the "verify
  // directions, expose flip toggle" discipline.
  readonly uEarthDir = uniform(new THREE.Vector3(0.45, 0.12, -0.88).normalize());
  // Earth's angular radius on the sky. Real value from the Moon is ~0.009
  // rad (1°). We render it bigger (~3°) so the continents are legible and
  // the whole map reads as "look where we are".
  readonly uEarthAngularRadius = uniform(0.055);
  // Direction *toward* the sun from Earth's centre, in world space. Drives
  // the day/night terminator on Earth. Tuned visually so the shadow
  // crescent covers roughly the bottom 40% of the disk: sun points mostly
  // straight up (big y) with a tiny camera-side z component so the
  // terminator sits ~20% below disk centre rather than at the limb, and
  // x=-1 keeps the continents' lit side slightly offset so the shading
  // reads as "real world" rather than cardinal-aligned.
  readonly uEarthSunDir = uniform(new THREE.Vector3(-1, 7, 2).normalize());
  // Radians per second of Earth's axial spin as seen from the moon. Real
  // value is 2π / 86400 ≈ 7.3e-5. We boost ~400× so the continents visibly
  // rotate on a gameplay timescale without being dizzying (~5 min per full
  // rotation).
  readonly uEarthSpinRate = uniform(0.02);
  // Earth spin axis in world space. Tilted ~23° like the real thing — a
  // small cosmetic nicety, keeps the polar ice caps slightly offset from
  // the poles of the disk as it rotates.
  readonly uEarthAxis = uniform(new THREE.Vector3(0.0, 0.97, -0.24).normalize());

  // Direction toward the sun in world space (i.e. position-from-origin where
  // the sun disc renders). Mirrored against `uEarthDir` in X and Z but
  // SHARES its Y so the sun and Earth sit at the same elevation above the
  // horizon — the user couldn't see the sun when it was high overhead, and
  // a flat angle reads more naturally for "the source of the long shadows
  // the level already casts" anyway.
  readonly uSunDir = uniform(new THREE.Vector3(-0.45, 0.12, 0.88).normalize());
  // Sun's angular radius. Real angular size from the moon is ~0.5°
  // (~0.009 rad). Rendered slightly bigger (1.7°) so the disc reads as a
  // recognisable sun against the deep-black sky rather than a star.
  readonly uSunAngularRadius = uniform(0.03);
  // Sun colour. Linear-space, peaks well above 1 so bloom catches the disc.
  readonly uSunColor = uniform(new THREE.Vector3(2.6, 2.4, 2.0));

  constructor(opts: MoonSkyOptions = {}) {
    super();
    const showEarth = opts.showEarth ?? true;
    const showNebula = opts.showNebula ?? true;
    this.side = THREE.BackSide;
    this.depthWrite = false;
    this.depthFunc = THREE.LessEqualDepth;
    this.lights = false;

    this.vertexNode = Fn(() => {
      const viewPos = cameraViewMatrix.mul(vec4(positionLocal, 0));
      const clip = cameraProjectionMatrix.mul(vec4(viewPos.xyz, 1));
      return vec4(clip.xy, clip.w, clip.w);
    })();

    // ------------------------------------------------------------------ utils

    const hash3 = Fn((args: [Node<'vec3'>]) => {
      const [p] = args;
      const q = vec3(
        p.dot(vec3(127.1, 311.7, 74.7)),
        p.dot(vec3(269.5, 183.3, 246.1)),
        p.dot(vec3(113.5, 271.9, 124.6))
      );
      return fract(sin(q).mul(43758.5453));
    });

    const noise3 = Fn((args: [Node<'vec3'>]) => {
      const [p] = args;
      const i = floor(p).toVar();
      const f = fract(p).toVar();
      const u = f.mul(f).mul(float(3).sub(f.mul(2)));

      const sample = (ox: number, oy: number, oz: number) =>
        hash3(i.add(vec3(ox, oy, oz))).x;

      const n000 = sample(0,0,0), n100 = sample(1,0,0);
      const n010 = sample(0,1,0), n110 = sample(1,1,0);
      const n001 = sample(0,0,1), n101 = sample(1,0,1);
      const n011 = sample(0,1,1), n111 = sample(1,1,1);

      const nx00 = mix(n000, n100, u.x);
      const nx10 = mix(n010, n110, u.x);
      const nx01 = mix(n001, n101, u.x);
      const nx11 = mix(n011, n111, u.x);
      const nxy0 = mix(nx00, nx10, u.y);
      const nxy1 = mix(nx01, nx11, u.y);
      return mix(nxy0, nxy1, u.z);
    });

    const fbm3 = Fn((args: [Node<'vec3'>]) => {
      const [p] = args;
      const q = p.toVar();
      const amp = float(0.5).toVar();
      const sum = float(0).toVar();
      for (let i = 0; i < 5; i++) {
        sum.addAssign(noise3(q).mul(amp));
        q.assign(q.mul(2.03).add(vec3(13.1, 23.7, 7.2)));
        amp.assign(amp.mul(0.54));
      }
      return sum;
    });

    // Higher-frequency fbm used for continental detail. Same recursion but
    // tuned to converge faster so fewer octaves waste cycles on sub-pixel
    // noise once the signal is projected into the ~2° Earth disk.
    const fbm3Tight = Fn((args: [Node<'vec3'>]) => {
      const [p] = args;
      const q = p.toVar();
      const amp = float(0.5).toVar();
      const sum = float(0).toVar();
      for (let i = 0; i < 4; i++) {
        sum.addAssign(noise3(q).mul(amp));
        q.assign(q.mul(2.17).add(vec3(7.2, 13.1, 23.7)));
        amp.assign(amp.mul(0.5));
      }
      return sum;
    });

    // Per-cell star disk. Size range is intentionally narrow now (~1.0-2.0×
    // the smallest star instead of the old 5× spread) — the previous spread
    // produced oversized bloom-bait "blobs" that read as streetlights more
    // than stars. The pow exponent is tightened (2.5 → 4.5) to shrink the
    // soft halo so bloom picks up a crisp core instead of a wide smear.
    // Twinkle amplitude is raised (±0.25 → ±0.45) and two detuned sines
    // with independent hash-phases make each star flicker on its own
    // schedule rather than the whole sky breathing together.
    const starLayer = Fn((args: [Node<'vec3'>, Node<'float'>, Node<'float'>, Node<'float'>]) => {
      const [d, scale, threshold, sizeScale] = args;
      const p = d.mul(scale);
      const cell = floor(p);
      const f = fract(p);
      const h = hash3(cell);
      const centre = vec3(0.5).add(h.sub(0.5).mul(0.7));
      const dist = length(f.sub(centre));
      const mask = step(threshold, h.x);
      // Two detuned sines with different per-cell phase offsets so stars
      // twinkle independently (one slow component, one faster). Combined
      // amplitude reaches ±0.45 so dim stars nearly disappear at their
      // trough — the liveliness the user asked for.
      const twinkleA = sin(this.uTime.mul(2.3).add(h.y.mul(25.0))).mul(0.28);
      const twinkleB = sin(this.uTime.mul(5.1).add(h.z.mul(41.0))).mul(0.17);
      const twinkle = twinkleA.add(twinkleB).add(0.62);
      // Cell-unit size 0.07–0.11 (was 0.12–0.30) × sizeScale.
      const size = h.z.mul(0.04).add(0.07).mul(sizeScale);
      const disk = pow(max(float(1).sub(dist.div(max(size, 0.0001))), 0), 4.5);
      return disk.mul(mask).mul(twinkle);
    });

    // Rotate a 3D point `p` around `axis` (unit vector) by angle `ang`.
    // Rodrigues' formula: p_rot = p*cos + (axis × p)*sin + axis*(axis·p)*(1-cos).
    // Used for Earth's axial spin so the continent fbm advects round the
    // disk as uTime progresses.
    const rotateAxis = Fn((args: [Node<'vec3'>, Node<'vec3'>, Node<'float'>]) => {
      const [p, axis, ang] = args;
      const c = cos(ang);
      const s = sin(ang);
      const cross = vec3(
        axis.y.mul(p.z).sub(axis.z.mul(p.y)),
        axis.z.mul(p.x).sub(axis.x.mul(p.z)),
        axis.x.mul(p.y).sub(axis.y.mul(p.x))
      );
      const d = axis.dot(p);
      return p.mul(c).add(cross.mul(s)).add(axis.mul(d).mul(float(1).sub(c)));
    });

    // ---------------------------------------------------------- earth sampler
    //
    // Returns vec4(rgb, alpha-disk). `alpha` is 1 inside the earth disk
    // (after smooth edge) and 0 outside; the caller composites this over
    // the base space sky. The atmospheric rim/halo is returned as a
    // separate add-on so it can extend past the disk edge.
    //
    // Math: the sky is a unit sphere; Earth is a sphere of radius sin(r) at
    // distance 1 in direction earthDir. Ray-sphere intersection gives the
    // surface point for the view ray, from which we derive an outward
    // normal, sample continents/oceans/clouds in 3D, and light it against
    // the sun.
    const earthSample = Fn((args: [Node<'vec3'>]) => {
      const [d] = args;

      const earthDir = normalize(this.uEarthDir);
      const sunDir = normalize(this.uEarthSunDir);
      const axis = normalize(this.uEarthAxis);
      const r = this.uEarthAngularRadius;
      const sinR = sin(r);
      const cosR = cos(r);

      const cosT = d.dot(earthDir).toVar();

      // Disk mask: smooth edge right at the limb so the disk anti-aliases
      // into the sky.
      const disk = smoothstep(cosR.sub(0.0008), cosR.add(0.0008), cosT);

      // Ray-sphere: t² - 2t·cosT + cos²(r) = 0.
      // Inside the disk cosT ≥ cos(r), so the discriminant cosT² - cos²(r) ≥ 0.
      // Outside we clamp to 0 (the result is unused — disk mask is 0).
      const disc = max(cosT.mul(cosT).sub(cosR.mul(cosR)), float(0)).toVar();
      const tHit = cosT.sub(disc.sqrt()).toVar();

      // Surface point P on the earth, outward normal N (unit length).
      const P = d.mul(tHit);
      const N = P.sub(earthDir).div(max(sinR, 0.0001)).toVar();

      // Rotate the surface normal backwards by the spin angle so the
      // continent fbm stays anchored to "Earth space" while time advances.
      const spinAng = this.uTime.mul(this.uEarthSpinRate);
      const Nspin = rotateAxis(N, axis, negate(spinAng)).toVar();

      // Latitude proxy: projection of the spin-rotated normal onto the
      // spin axis gives cos(90°−lat) = sin(lat). We use |·| for the polar
      // cap mask, symmetric around the equator.
      const latSin = Nspin.dot(axis).toVar();

      // ---- Continental fbm ---------------------------------------------
      // 3D fbm on the spin-rotated normal. Base frequency chosen so a
      // continent spans ~30° of Earth's surface — readable at 3° apparent
      // angular radius.
      const landFbm = fbm3(Nspin.mul(3.2)).toVar();
      // Sharp coastline edges. Previously 0.48–0.56 feathered coasts over
      // a wide band which read as watercolour; narrowing to 0.51–0.54
      // gives crisp continent outlines like real satellite imagery.
      const landMask = smoothstep(0.51, 0.54, landFbm).toVar();

      // Biome variation: a second fbm shifts shading between arid browns
      // and tropical greens so the continents aren't monochrome. Values
      // slightly desaturated vs the original overshoot — real satellite
      // Earth is more muted than the vivid "globe graphic" colour read.
      const biomeFbm = fbm3Tight(Nspin.mul(6.0).add(vec3(17.1, 4.3, 9.7)));
      const landDesert = vec3(0.40, 0.34, 0.23);
      const landForest = vec3(0.12, 0.26, 0.13);
      const landHigh   = vec3(0.54, 0.48, 0.38); // mountain/steppe
      const landColor = mix(
        mix(landForest, landDesert, smoothstep(0.35, 0.65, biomeFbm)),
        landHigh,
        smoothstep(0.68, 0.90, landFbm) // high elevation = tops of continents
      );

      // ---- Oceans -------------------------------------------------------
      // Slightly lighter than satellite-deep-blue so the continents stand
      // against a more reading-friendly background without pushing the
      // disk into cartoon-turquoise territory.
      const oceanBase = vec3(0.06, 0.16, 0.36);
      const oceanWarm = vec3(0.10, 0.28, 0.50); // shallow / tropical shelf
      // Shallower water near coastlines: proximity-to-land detection via
      // the landFbm gradient, approximated by how close the fbm value is
      // to the land threshold. Close to threshold ⇒ shallow.
      const coastProx = float(1).sub(abs(landFbm.sub(0.50)).mul(6.0));
      const shallow = smoothstep(0.3, 0.9, coastProx).mul(float(1).sub(landMask));
      const oceanColor = mix(oceanBase, oceanWarm, shallow);

      // ---- Polar ice caps ----------------------------------------------
      // Wider over land (Antarctica), tighter over ocean (arctic sea ice
      // is seasonal and smaller). Uses |latSin| so both hemispheres cap.
      const iceCap = smoothstep(0.72, 0.92, abs(latSin));
      const iceOverLand = iceCap.mul(landMask.mul(0.5).add(0.5));
      const surfaceBase = mix(oceanColor, landColor, landMask);
      const withIce = mix(surfaceBase, vec3(0.95, 0.97, 1.00), iceOverLand);

      // ---- Clouds -------------------------------------------------------
      // Large swirling weather systems (hurricanes, frontal bands) rather
      // than the previous uniform speckle. Two techniques stacked:
      //
      //   1. Domain-warp the cloud coordinate by a low-frequency vec3 noise
      //      field. That's what gives real cloud-cover maps their curling
      //      IQ-swirl shape (jet-stream fingers, spiral cyclones).
      //   2. Drop the base frequency 7.5 → 3.0 so a single cloud system
      //      spans ~30% of the Earth disk — matching satellite imagery
      //      more than the old "fine mist" pattern.
      //
      // Spin rate still 1.4× the ground so the weather drifts visibly
      // against the continents.
      const cloudAxis = axis;
      const cloudAng = this.uTime.mul(this.uEarthSpinRate.mul(1.4));
      const Ncloud = rotateAxis(N, cloudAxis, negate(cloudAng));

      // Warp coordinate: three cheap value-noise samples at a lower
      // frequency form a vector field that bends the sampling grid.
      // Centred on 0 (noise3 is [0,1], subtract 0.5).
      const warpP = Ncloud.mul(2.2);
      const warpA = noise3(warpP).sub(0.5);
      const warpB = noise3(warpP.add(vec3(13.1, 7.3, 23.2))).sub(0.5);
      const warpC = noise3(warpP.add(vec3(5.1, 19.4, 31.7))).sub(0.5);
      const cloudWarp = vec3(warpA, warpB, warpC).mul(1.4);

      const cloudFbm = fbm3Tight(Ncloud.mul(3.0).add(cloudWarp).add(vec3(3.1, 9.4, 5.8)));
      // Grazing-angle fade. Without this, clouds near the limb stayed at
      // full brightness and blended visually with the blue atmospheric
      // halo — they read as "clouds floating in the atmosphere shimmer"
      // rather than sitting on the globe. Fading them by the camera-
      // facing term of the surface normal anchors them to the sphere.
      const cloudFacing = smoothstep(0.05, 0.35, max(N.dot(negate(d)), float(0)));
      const cloudMask = smoothstep(0.55, 0.72, cloudFbm).mul(0.85).mul(cloudFacing);
      // Slight cool off-white — pure (1,1,1) clouds read as emissive /
      // detached-from-surface; a faint blue-grey tint grounds them as a
      // real atmospheric layer catching the same sunlight as the land.
      const cloudColor = vec3(0.93, 0.95, 0.97);
      const withClouds = mix(withIce, cloudColor, cloudMask);

      // ---- Day / night + shading ---------------------------------------
      // ndl against the world-space sun direction (N is a world-space
      // outward normal, sunDir is a world-space direction toward the sun
      // from Earth's centre). Dark side goes to a faint night-ambient
      // rather than pitch black so the silhouette reads.
      const ndl = N.dot(sunDir).toVar();
      // Sharp terminator. Real Earth-from-space photos show a crisp
      // day/night seam (the atmospheric scattering zone is only a few
      // hundred km wide, so it subtends ~0.05 rad on the 6400 km globe).
      // We model this as a narrow smoothstep gate + a mild cosine-shaped
      // falloff within the day side, rather than the old linear Lambert —
      // linear produced a visibly blurred half-lit band that read as
      // "soft lighting" rather than "daylit Earth against space".
      const daySide = smoothstep(-0.015, 0.05, ndl).toVar();
      const dayFalloff = max(ndl, float(0)).mul(0.7).add(0.35);
      const dayLit = daySide.mul(dayFalloff);
      // Dusky terminator tint: a narrow band near ndl≈0 picks up a warm
      // red/orange scattering colour that reads as the atmosphere grazing
      // the setting/rising sun.
      const terminator = smoothstep(0.18, 0.0, abs(ndl))
        .mul(smoothstep(-0.25, -0.05, ndl));
      const terminatorColor = vec3(0.95, 0.35, 0.12);

      // No baseline ambient on the day side — the `dayFalloff` floor of
      // 0.35 already prevents the daylit region going too dark at its
      // edges, and any added floor blurs the terminator.
      const dayColor = withClouds.mul(dayLit.mul(1.15));

      // ---- Night: city lights -----------------------------------------
      // Only on land, only on night side, kept below the bloom threshold.
      // Two-layer gating: a coarse fbm picks "populated regions" (eastern
      // seaboard scale) and a medium-scale noise threshold picks individual
      // metro clusters inside those regions. The product gives tight
      // clumps of lights rather than uniformly sprinkled pixels.
      //
      // The cloud-occlusion multiplier from the previous version is
      // intentionally removed: clouds drift at a different rate than the
      // ground, so any city under a moving cloud field would flicker on
      // and off. With clouds gone from the mask, lights stay stable as
      // Earth rotates — they translate with the continents but don't
      // twinkle.
      const nightMask = smoothstep(0.05, -0.20, ndl);
      const cityCoarse = fbm3Tight(Nspin.mul(6.0).add(vec3(47.3, 11.8, 5.2)));
      const cityLocal = noise3(Nspin.mul(26.0));
      // Thresholds loosened vs prior pass — the night side read as too
      // sparse (big dark regions across whole continents). These values
      // roughly double the area that qualifies while keeping the two-
      // layer gating so the result stays in discrete clumps rather than
      // turning into a uniform carpet.
      const cityThresh = smoothstep(0.42, 0.60, cityCoarse)
        .mul(smoothstep(0.62, 0.82, cityLocal));
      // Half the previous intensity so the night side reads populated but
      // not carnival-lit.
      const cityColor = vec3(0.35, 0.24, 0.08);
      // Camera-facing fade: N points outward from Earth's centre, -d is
      // the view direction back toward the camera, so N·(-d) is 1 at the
      // disk centre and 0 at the limb. This foreshortening makes cities
      // near the edge dim out, matching how real terminator photographs
      // look.
      const facing = max(N.dot(negate(d)), float(0));
      const facingFade = smoothstep(0.05, 0.35, facing);
      const cityLights = cityColor
        .mul(cityThresh)
        .mul(landMask)
        .mul(nightMask)
        .mul(facingFade);
      // Night side also has a subtle blue-grey ambient so the ocean
      // silhouette is faintly visible against space.
      const nightAmbient = withClouds.mul(0.03).mul(nightMask);

      // ---- Specular ocean sheen ---------------------------------------
      // The sub-solar point on the ocean catches a faint glint. Kept much
      // weaker than the previous "bright pinpoint" version — real Earth-
      // from-space photos show oceans as mostly matte diffuse reflectors,
      // with specular only visible on the exact sub-solar crescent. We
      // also narrow the highlight (exponent 90 → 140) so it stays
      // pinpoint rather than a broad lobe.
      const viewDir = d;
      const nDotS = ndl;
      const R = sunDir.sub(N.mul(nDotS.mul(2.0)));
      const spec = pow(max(viewDir.dot(R), 0), float(140))
        .mul(float(1).sub(landMask))
        .mul(float(1).sub(cloudMask))
        .mul(smoothstep(0.05, 0.3, ndl));
      const specColor = vec3(0.30, 0.28, 0.24).mul(spec);

      // Combine everything visible inside the disk.
      const body = dayColor
        // Terminator atmospheric fringe — previously 0.45 multiplier read
        // as a pronounced orange band across the disk. Dropped ~10× so
        // it's a subtle warm tint right at the ndl≈0 seam.
        .add(terminatorColor.mul(terminator).mul(0.045))
        .add(cityLights)
        .add(nightAmbient)
        .add(specColor);

      return vec4(body, disk);
    });

    // Soft blue atmospheric halo that extends just past the disk edge.
    // Separate from earthSample() because it needs to be additive even
    // outside the disk mask.
    const earthHalo = Fn((args: [Node<'vec3'>]) => {
      const [d] = args;
      const earthDir = normalize(this.uEarthDir);
      const sunDir = normalize(this.uEarthSunDir);
      const r = this.uEarthAngularRadius;
      const cosT = d.dot(earthDir);
      const cosR = cos(r);
      // Atmosphere extends ~4.4% beyond the planet limb (was 22% — user
      // feedback: the glow was ~5× wider than it should be). That ratio
      // is close to Earth's real atmosphere-to-radius proportion.
      const cosRouter = cos(r.mul(1.044));

      // Ramp from the outer atmosphere boundary (cosRouter) to the planet
      // limb (cosR) — peaks just outside the disk. The second factor
      // zeroes the halo once the ray is inside the disk (where the Earth
      // surface takes over the colour).
      const insideDisk = step(cosR, cosT);
      const haloRamp = smoothstep(cosRouter, cosR, cosT)
        .mul(float(1).sub(insideDisk));

      // Atmosphere is brighter on the sun-facing side. Approximate the
      // limb's normal by the in-plane offset direction from earthDir.
      const perp = d.sub(earthDir.mul(cosT));
      const limbN = normalize(perp.add(earthDir.mul(0.0001)));
      const sideLit = max(limbN.dot(sunDir), float(0));

      const haloColor = vec3(0.35, 0.55, 1.00);
      return haloColor.mul(haloRamp).mul(sideLit.mul(1.5).add(0.15));
    });

    // ---------------------------------------------------------------- main

    this.colorNode = Fn(() => {
      const d = positionLocal.normalize().toVar();
      const h = d.y;

      // Deep-space base. Darker than the sun map's sky — no atmosphere to
      // scatter red/orange into the horizon. A hint of cool purple at
      // mid-altitude keeps the void from being pitch black everywhere.
      // When showNebula is off we want the sky to read as *deep* space
      // (no purple tinge), so both stops collapse to near-zero.
      const zenith = showNebula ? vec3(0.002, 0.002, 0.010) : vec3(0.0, 0.0, 0.0);
      const midSky = showNebula ? vec3(0.012, 0.008, 0.030) : vec3(0.0, 0.0, 0.0);
      const spaceBase = mix(midSky, zenith, smoothstep(0.2, 1.0, h));

      // Below-horizon fade so the moon surface (which fills the lower
      // hemisphere) doesn't render through the sky.
      const belowHorizon = smoothstep(-0.18, -0.02, h);

      // -------- Nebulae ----------------------------------------------------
      // Palette: red / purple / blue — cyan has been removed because the
      // previous bright cyan/teal blobs read as the dominant feature,
      // which the starfield should be. Colour is selected by `nebC` as a
      // three-way gradient so you get solid-red, solid-purple, solid-blue
      // regions plus the in-between mixes (red→purple, purple→blue).
      //
      // Brightness: we dropped the pow(nebShape, 3.0) "bright core" term
      // that the previous version used to push nebula peaks through the
      // bloom threshold. The soft large-scale structure (the "hardest to
      // see" layer the user flagged as correct) lives entirely in the
      // linear multiplier; losing the core term removes the pronounced
      // small-cloud blobs without erasing the subtle backdrop.
      const nebA = fbm3(d.mul(1.9)).toVar();
      const nebB = fbm3(d.mul(3.4).add(vec3(11.0, 5.0, 23.0))).toVar();
      const nebC = fbm3(d.mul(1.3).add(vec3(47.0, 12.0, 91.0))).toVar();
      // Thresholds loosened vs the previous overly-strict pass so more
      // of the sky qualifies as nebula — user reported only catching
      // one blue patch across the whole dome.
      const nebShape = smoothstep(0.58, 0.85, nebA).mul(smoothstep(0.48, 0.78, nebB));
      // Per-colour balance — matches the sun-map palette so the two skies
      // read consistently. Purple pulled down (was dominating), red
      // boosted further (was invisible), selector thresholds widened on
      // red side since most `nebC` values cluster near 0.5 and the old
      // 0.25–0.50 band caught almost no pixels.
      const nebRed    = vec3(1.20, 0.20, 0.22);
      const nebPurple = vec3(0.55, 0.14, 0.90);
      const nebBlue   = vec3(0.12, 0.36, 1.10);
      const nebHue = mix(
        mix(nebRed, nebPurple, smoothstep(0.42, 0.58, nebC)),
        nebBlue,
        smoothstep(0.62, 0.80, nebC)
      );
      // Brightness ~6× the initial desaturation pass — user couldn't see
      // the nebulae at lower multipliers. Linear-only compose (no pow
      // core term) keeps the soft diffuse character even at high values;
      // peak channels now reach the bloom threshold (~0.85) so bright
      // nebula regions pick up a gentle glow, aiding visibility without
      // the sharp blob read of the previous pow-core version.
      const nebBright = nebShape.mul(0.82);
      // Fade nebula near the Earth disk. Without this, the nebula field
      // happens to drop bright patches right next to (or framing) the
      // Earth, which reads as Earth "in front of a coloured cloud"
      // instead of floating in clean space. Fade begins at ~9° from the
      // Earth centre and reaches max suppression at the disk limb.
      // Skipped entirely in earth-less mode — there's no disk to avoid.
      let earthNebFade: Node<'float'> = float(1);
      if (showEarth) {
        const earthDirN = normalize(this.uEarthDir);
        const earthCosT = d.dot(earthDirN);
        // Fade band extended further out — user reported one nebula still
        // sitting uncomfortably close to Earth. Outer boundary 4× → 8×
        // (~25° from Earth centre), inner 1.2× → 1.1×, suppression 92% → 98%.
        const earthProx = smoothstep(cos(this.uEarthAngularRadius.mul(8.0)),
                                     cos(this.uEarthAngularRadius.mul(1.1)),
                                     earthCosT);
        earthNebFade = float(1).sub(earthProx.mul(0.98));
      }
      const nebula = nebHue.mul(nebBright)
        .mul(smoothstep(0.00, 0.12, h))
        .mul(earthNebFade);

      // -------- Stars ------------------------------------------------------
      // sizeScale values brought closer together (was 1.4 / 1.0 / 0.7 →
      // now 1.0 / 0.9 / 0.75) so the largest stars are ~1.3× the smallest,
      // combined with the tighter per-star size range in starLayer() this
      // keeps the whole population inside a 2× size window as requested.
      // Density loosened another 5× on top of the prior pass — user still
      // wanted more stars. 0.970 → 0.85 (3% → 15%), 0.940 → 0.70 (6% →
      // 30%), distant haze 0.976 → 0.88 (2.4% → 12%).
      const stars1 = starLayer(d, 55,  0.85, 1.00);
      const stars2 = starLayer(d, 130, 0.70, 0.90);
      const stars3 = starLayer(d, 280, 0.88, 0.75); // distant haze of pinpoints
      const starSum = stars1.mul(1.8).add(stars2.mul(1.1)).add(stars3.mul(0.7));
      const starCol = vec3(1.30, 1.20, 1.05).mul(starSum);
      const starFade = smoothstep(-0.04, 0.04, h);
      const stars = starCol.mul(starFade);

      // When the nebula layer is disabled we compose only stars over the
      // black spaceBase — keeps the splash cosmos clean of coloured clouds.
      const skyBase = showNebula ? spaceBase.add(nebula).add(stars) : spaceBase.add(stars);

      // -------- Earth disk + halo ------------------------------------------
      // Skipped in earth-less mode; the splash screen uses the pure
      // starfield+nebula backdrop.
      let withEarth: Node<'vec3'> = skyBase;
      if (showEarth) {
        const earth = earthSample(d);
        const halo = earthHalo(d);
        withEarth = mix(skyBase, earth.xyz, earth.w).add(halo);
      }

      // -------- Sun disc + soft glow ---------------------------------------
      // Bright disc at `uSunDir` plus a wider falloff. Disc edge softened
      // ~5% of the radius so it doesn't alias hard against the starfield.
      // The glow fades over ~6× the disc radius and contributes an
      // additional warm bias to the surrounding sky — sells the
      // "atmosphere-less point source casting the long shadows you see in
      // the level".
      const sunDir = normalize(this.uSunDir);
      const cosT = d.dot(sunDir);
      const cosR = cos(this.uSunAngularRadius);
      const cosREdge = cos(this.uSunAngularRadius.mul(0.95));
      const cosRGlow = cos(this.uSunAngularRadius.mul(6.0));
      const disc = smoothstep(cosR, cosREdge, cosT);
      const glow = pow(smoothstep(cosRGlow, cosR, cosT), float(2.0));
      const sun = this.uSunColor.mul(disc.add(glow.mul(0.18)));

      const composed = withEarth.add(sun);
      const final = composed.mul(belowHorizon);
      return vec4(final, 1);
    })();
  }

  setTime(t: number): void { this.uTime.value = t; }
}
